import { useState } from "react";
import { AlertTriangle, Building2, Loader2, Plus, ShieldAlert, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  useAdminLegalEntities,
  useCreateLegalEntity,
  useUpdateLegalEntity,
} from "@/hooks/useAdminLegalEntities";
import type {
  AdminLegalEntitiesResponse,
  AdminLegalEntityPayload,
  AdminLegalEntityRow,
} from "@/api/adminCenter";

/**
 * G2-S3 (D2=C) — Admin Center → Юрособи Kormax (visual ③).
 * Admin-only governed directory: status / availability / allowedTeams /
 * allowedUsers / aliases. Backend AvailabilityGuard enforces status validity +
 * never-zero-default. Edit denied for managers (S1 ACL + server guard).
 */

// D-541 AD-2 (§20): UA-лейбли статусів — display-only, технічні значення стабільні.
const STATUS_LABEL: Record<string, string> = {
  active: "активна",
  inactive: "неактивна",
  historical: "історична",
};

function StatusBadge({ status }: { status: string }) {
  if (status === "active")
    return <Badge variant="success">активна</Badge>;
  if (status === "inactive")
    return (
      <span className="inline-flex items-center rounded-full bg-warning px-2 py-0.5 text-[12px] font-medium text-warning-foreground">
        неактивна
      </span>
    );
  return (
    <span className="inline-flex items-center rounded-full bg-destructive/10 px-2 py-0.5 text-[12px] font-medium text-destructive">
      історична
    </span>
  );
}

function availabilityText(le: AdminLegalEntityRow): string {
  if (le.status !== "active" || !le.availableForNewContracts) return "недоступна для нових";
  if (le.defaultForAll) return "доступна всім";
  const parts: string[] = [];
  if (le.allowedTeams.length) parts.push(`${le.allowedTeams.length} команд`);
  if (le.allowedUsers.length) parts.push(`${le.allowedUsers.length} менеджерів`);
  return parts.length ? parts.join(" + ") : "лише адмін";
}

export function AdminLegalEntitiesPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;

  const q = useAdminLegalEntities(canView);
  const [editId, setEditId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    window.setTimeout(() => setToast(null), 4000);
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const data = q.data;
  const editing = editId ? data?.items.find((i) => i.id === editId) ?? null : null;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1180px]">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <div>
              <h1 className="text-lg font-semibold">Юрособи Kormax</h1>
              <p className="text-xs text-muted-foreground">
                Керований довідник юросіб · доступність для нових договорів керується тут
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="mr-1 h-4 w-4" /> Додати
          </Button>
        </div>

        {/* seed-dedup forward-guard banner (§0 = 0 real dups today) */}
        {!!data?.dupCandidates.length && (
          <div className="mb-3 flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Можливий дубль з 1С:{" "}
              {data.dupCandidates
                .map((d) => d.members.map((m) => `«${m.name}»`).join(" / "))
                .join("; ")}{" "}
              — перевірте перед seed/apply (seed-dedup).
            </span>
          </div>
        )}

        <div className="rounded-lg border bg-card">
          {q.isLoading ? (
            <div className="flex items-center justify-center p-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Завантаження…
            </div>
          ) : q.isError ? (
            <div className="p-10 text-center text-sm text-destructive">Помилка завантаження.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 font-medium">Назва</th>
                  <th className="px-2 py-2.5 font-medium">Статус</th>
                  <th className="px-2 py-2.5 font-medium">Доступність</th>
                  <th className="px-2 py-2.5 text-right font-medium">Договорів</th>
                  <th className="px-2 py-2.5 font-medium">Остання</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((le) => (
                  <tr
                    key={le.id}
                    onClick={() => setEditId(le.id)}
                    className={cn(
                      "cursor-pointer border-t transition-colors hover:bg-accent/50",
                      le.status !== "active" && "opacity-70",
                    )}
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium">{le.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {le.legalName}
                        {le.edrpou ? ` · ЄДРПОУ ${le.edrpou}` : " · ЄДРПОУ —"}
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <StatusBadge status={le.status} />
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground">{availabilityText(le)}</td>
                    <td className="px-2 py-2.5 text-right tabular-nums">
                      {le.linkedContracts.toLocaleString("uk-UA")}
                    </td>
                    <td className="px-2 py-2.5 tabular-nums text-muted-foreground">
                      {le.lastUsed ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {(editing || creating) && data && (
        <LegalEntityDrawer
          le={editing}
          options={data.options}
          onClose={() => {
            setEditId(null);
            setCreating(false);
          }}
          onToast={showToast}
        />
      )}

      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-4 right-4 z-50 rounded-lg px-4 py-2.5 text-sm shadow-lg",
            toast.kind === "ok" ? "bg-success text-success-foreground" : "bg-destructive-soft text-destructive",
          )}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

// ─── Drawer (create / edit) ──────────────────────────────────────────────────

function LegalEntityDrawer({
  le,
  options,
  onClose,
  onToast,
}: {
  le: AdminLegalEntityRow | null;
  options: AdminLegalEntitiesResponse["options"];
  onClose: () => void;
  onToast: (kind: "ok" | "err", msg: string) => void;
}) {
  const isCreate = !le;
  const panelRef = useDrawerA11y(true, onClose);
  const createM = useCreateLegalEntity();
  const updateM = useUpdateLegalEntity();

  const [name, setName] = useState(le?.name ?? "");
  const [legalName, setLegalName] = useState(le?.legalName ?? "");
  const [edrpou, setEdrpou] = useState(le?.edrpou ?? "");
  const [status, setStatus] = useState<string>(le?.status ?? "active");
  const [availableForNewContracts, setAvailable] = useState(le?.availableForNewContracts ?? true);
  const [defaultForAll, setDefaultForAll] = useState(le?.defaultForAll ?? false);
  const [teamIds, setTeamIds] = useState<string[]>(le?.allowedTeamsIds ?? []);
  const [userIds, setUserIds] = useState<string[]>(le?.allowedUsersIds ?? []);
  const [aliases, setAliases] = useState(le?.aliases ?? "");
  const [validFrom, setValidFrom] = useState(le?.validFrom ?? "");
  const [validTo, setValidTo] = useState(le?.validTo ?? "");

  const toggle = (arr: string[], id: string) =>
    arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];

  const pending = createM.isPending || updateM.isPending;

  const willDisableWithContracts =
    !isCreate &&
    (le?.linkedContracts ?? 0) > 0 &&
    le?.status === "active" &&
    (status !== "active" || !availableForNewContracts);

  const submit = () => {
    if (!name.trim() || !legalName.trim()) {
      onToast("err", "Назва та Legal name — обовʼязкові.");
      return;
    }
    if (willDisableWithContracts) {
      const ok = window.confirm(
        `Вимкнення «${le?.name}» (${le?.linkedContracts} договорів): старі лишаються read-only, нові з нею не створюються. Підтвердити?`,
      );
      if (!ok) return;
    }
    const body: AdminLegalEntityPayload = {
      name: name.trim(),
      legalName: legalName.trim(),
      edrpou: edrpou.trim() || null,
      status,
      availableForNewContracts,
      defaultForAll,
      allowedTeamsIds: teamIds,
      allowedUsersIds: userIds,
      aliases: aliases.trim() || null,
      validFrom: validFrom || null,
      validTo: validTo || null,
    };
    const onResult = {
      onSuccess: () => {
        onToast("ok", isCreate ? "Юр.особу створено" : "Збережено");
        onClose();
      },
      onError: (e: unknown) => onToast("err", e instanceof Error ? e.message : "Помилка"),
    };
    if (isCreate) createM.mutate(body, onResult);
    else updateM.mutate({ id: le!.id, body }, onResult);
  };

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={isCreate ? "Нова юр.особа" : `Юр.особа ${le?.name}`}
        className="absolute right-0 top-0 h-full w-[420px] overflow-y-auto border-l bg-card p-5 shadow-xl outline-none"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{isCreate ? "Нова юр.особа" : le?.name}</h2>
          <button onClick={onClose} aria-label="Закрити" className="rounded p-1 hover:bg-accent">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <Field label="Відображувана назва *">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Юридична назва *">
              <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} />
            </Field>
            <Field label="ЄДРПОУ / ІПН">
              <Input value={edrpou} onChange={(e) => setEdrpou(e.target.value)} placeholder="—" />
            </Field>
          </div>

          <Field label="Статус">
            <Select value={status} onChange={setStatus} options={options.statuses.map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s }))} />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Доступна для нових договорів">
              <Select
                value={availableForNewContracts ? "1" : "0"}
                onChange={(v) => setAvailable(v === "1")}
                options={[{ value: "1", label: "так" }, { value: "0", label: "ні" }]}
              />
            </Field>
            <Field label="Доступна всім (default)">
              <Select
                value={defaultForAll ? "1" : "0"}
                onChange={(v) => setDefaultForAll(v === "1")}
                options={[{ value: "0", label: "ні" }, { value: "1", label: "так" }]}
              />
            </Field>
          </div>

          <Field label="Дозволені команди">
            <ChipPicker
              options={options.teams}
              selected={teamIds}
              onToggle={(id) => setTeamIds((a) => toggle(a, id))}
              empty="команди не задано"
            />
          </Field>
          <Field label="Дозволені користувачі">
            <ChipPicker
              options={options.users}
              selected={userIds}
              onToggle={(id) => setUserIds((a) => toggle(a, id))}
              empty="менеджери не задані"
            />
          </Field>

          <Field label="Псевдоніми з 1С (для звірки дублів)">
            <textarea
              value={aliases}
              onChange={(e) => setAliases(e.target.value)}
              placeholder="кощак елена; фоп кощак о."
              className="h-16 w-full rounded-md border bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Чинна з">
              <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
            </Field>
            <Field label="Чинна до">
              <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
            </Field>
          </div>

          {!isCreate && (
            <p className="text-xs text-muted-foreground">
              договорів: {le?.linkedContracts.toLocaleString("uk-UA")} · останнє використання: {le?.lastUsed ?? "—"}
            </p>
          )}
          {willDisableWithContracts && (
            <div className="flex items-start gap-2 rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              Вимкнення активної юрособи з договорами: старі лишаються read-only, нові з нею не створюються.
            </div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          <Button size="sm" variant="outline" onClick={onClose} disabled={pending}>
            Скасувати
          </Button>
          <Button size="sm" onClick={submit} disabled={pending}>
            {pending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isCreate ? "Створити" : "Зберегти"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-9 w-full rounded-md border bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ChipPicker({
  options,
  selected,
  onToggle,
  empty,
}: {
  options: { id: string; name: string }[];
  selected: string[];
  onToggle: (id: string) => void;
  empty: string;
}) {
  if (!options.length) return <p className="text-xs text-muted-foreground">{empty}</p>;
  return (
    <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-md border bg-background p-2">
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(o.id)}
            className={cn(
              "rounded-full px-2.5 py-1 text-xs transition-colors",
              on
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
            )}
          >
            {o.name}
          </button>
        );
      })}
    </div>
  );
}
